import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import { useSearch } from 'src/context/SearchProvider/useSearch';
import * as Styles from './SearchBar.styles';
import { CloseRounded, Search } from '@mui/icons-material';
import SearchBarSuggestions from 'src/components/SearchBar/SearchBarSuggestions';
import { useTrackSearchHistoryEvents } from 'src/features/products/presentation/hooks/useTrackSearchHistoryEvents';

function SearchBar({ autoFocus = false }: { autoFocus?: boolean }) {
  const {
    searchTerm,
    handleSearchChange,
    clearSearch,
    searchHistory,
    filteredSearchHistory,
  } = useSearch();
  const { trackSearchHistoryView, trackSearchHistoryClick } =
    useTrackSearchHistoryEvents();
  const [isFocused, setIsFocused] = useState(false);

  const suggestions = useMemo(
    () => (searchTerm ? filteredSearchHistory : searchHistory),
    [searchHistory, filteredSearchHistory]
  );
  const showSuggestions = useMemo(
    () => isFocused && suggestions && suggestions.length > 0,
    [isFocused, suggestions]
  );

  useEffect(() => {
    if (showSuggestions) {
      trackSearchHistoryView({ history: suggestions!, input: searchTerm });
    }
  }, [showSuggestions, suggestions]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleSearchChange(e.target.value);
  };

  const handleHistoryItemClick = (historyValue: string) => {
    trackSearchHistoryClick({ input: searchTerm, clicked_term: historyValue });
    handleSearchChange(historyValue);
  };

  return (
    <Box width="100%">
      <Styles.Search>
        <Styles.StyledInputBase
          autoFocus={autoFocus}
          fullWidth
          placeholder="¿Qué pedimos hoy?"
          value={searchTerm}
          onChange={handleInputChange}
          size="small"
          autoComplete="off"
          onFocus={() => setIsFocused(true)}
          onBlur={() => setTimeout(() => setIsFocused(false), 200)}
          InputProps={{
            endAdornment: searchTerm ? (
              <IconButton size="small" color="inherit" onClick={clearSearch}>
                <CloseRounded />
              </IconButton>
            ) : (
              <Search fontSize="small" />
            ),
          }}
        />
        {showSuggestions && (
          <Styles.SuggestionsContainer>
            <SearchBarSuggestions
              suggestions={suggestions!}
              searchTerm={searchTerm}
              onSuggestionClick={handleHistoryItemClick}
            />
          </Styles.SuggestionsContainer>
        )}
      </Styles.Search>
    </Box>
  );
}

export default SearchBar;
